import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Camera,
  Send,
  AlertTriangle,
  Clock,
  MapPin,
  User,
  Calendar,
  CheckSquare
} from 'lucide-react';

const TeamChecklistPage = () => {
  const { qrCode } = useParams();
  const navigate = useNavigate();
  
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [teamNotes, setTeamNotes] = useState('');
  const [teamMemberName, setTeamMemberName] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    const fetchChecklist = async () => {
      try {
        const response = await fetch(`/api/checklist/checklist/${qrCode}`);
        const data = await response.json();
        
        if (data.success) {
          setChecklist(data.checklist);
          setTeamNotes(data.checklist.teamNotes || '');
          
          // Expand all categories by default
          const categories = {};
          data.checklist.checklistItems.forEach(item => {
            categories[item.category] = true;
          });
          setExpandedCategories(categories);
        } else {
          setError(data.message || 'Failed to load checklist');
        }
      } catch (error) {
        console.error('Error fetching checklist:', error);
        setError('Failed to load checklist. Please check your connection.');
      } finally {
        setLoading(false);
      }
    };

    fetchChecklist();
  }, [qrCode]);



  const updateChecklistItem = async (itemId, updates) => {
    try {
      setSaving(true);
      const response = await fetch(`/api/checklist/checklist/${qrCode}/item/${itemId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();
      if (data.success) {
        setChecklist(data.checklist);
      } else {
        alert('Failed to update item: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Failed to update item. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleItemToggle = (itemId, checked) => {
    updateChecklistItem(itemId, { checked });
  };

  const handleNotesUpdate = (itemId, notes) => {
    updateChecklistItem(itemId, { notes });
  };

  const handlePhotoUpload = async (itemId, file) => {
    // In a real implementation, you would upload to a cloud storage service
    // For now, we'll create a data URL
    const reader = new FileReader();
    reader.onload = (e) => {
      updateChecklistItem(itemId, { photo: e.target.result });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!teamMemberName.trim()) {
      alert('Please enter your name before submitting');
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(`/api/checklist/checklist/${qrCode}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          teamNotes,
          teamMemberName: teamMemberName.trim()
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(`Checklist submitted successfully! Completion: ${data.completionPercentage}%`);
        navigate('/team/success');
      } else {
        alert('Failed to submit checklist: ' + data.message);
      }
    } catch (error) {
      console.error('Error submitting checklist:', error);
      alert('Failed to submit checklist. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getCompletionStats = () => {
    if (!checklist) return { completed: 0, total: 0, percentage: 0 };
    
    const completed = checklist.checklistItems.filter(item => item.checked).length;
    const total = checklist.checklistItems.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    
    return { completed, total, percentage };
  };

  const groupItemsByCategory = () => {
    if (!checklist) return {};
    
    return checklist.checklistItems.reduce((groups, item) => {
      const category = item.category;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
      return groups;
    }, {});
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading inspection checklist...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const stats = getCompletionStats();
  const groupedItems = groupItemsByCategory();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Property Inspection Checklist</h1>
              <p className="text-gray-600 mt-1">Complete all inspection items</p>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <Clock className="w-4 h-4" />
                <span>QR Code: {qrCode.substring(0, 8)}...</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Inspection Details Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border p-6 mb-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Inspection Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <User className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-gray-900">{checklist.customerName}</p>
                <p className="text-sm text-gray-500">Customer</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <MapPin className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-gray-900">{checklist.propertyAddress}</p>
                <p className="text-sm text-gray-500">Property Address</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Calendar className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-gray-900">
                  {new Date(checklist.inspectionDate).toLocaleDateString()}
                </p>
                <p className="text-sm text-gray-500">Scheduled Date</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <CheckSquare className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-gray-900">
                  {stats.completed} / {stats.total} ({stats.percentage}%)
                </p>
                <p className="text-sm text-gray-500">Completion</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Progress Bar */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border p-4 mb-6"
        >
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Progress</span>
            <span className="text-sm text-gray-500">{stats.percentage}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${stats.percentage}%` }}
            ></div>
          </div>
        </motion.div>

        {/* Team Member Info */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border p-6 mb-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Member Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Name *
              </label>
              <input
                type="text"
                value={teamMemberName}
                onChange={(e) => setTeamMemberName(e.target.value)}
                placeholder="Enter your full name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assigned To
              </label>
              <input
                type="text"
                value={checklist.assignedTo}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
              />
            </div>
          </div>
        </motion.div>

        {/* Checklist Items by Category */}
        {Object.entries(groupedItems).map(([category, items], categoryIndex) => (
          <motion.div
            key={category}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + categoryIndex * 0.1 }}
            className="bg-white rounded-lg shadow-sm border mb-6"
          >
            <div 
              className="p-4 border-b cursor-pointer hover:bg-gray-50"
              onClick={() => toggleCategory(category)}
            >
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">{category}</h3>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-500">
                    {items.filter(item => item.checked).length} / {items.length} completed
                  </span>
                  <button className="text-gray-400 hover:text-gray-600">
                    {expandedCategories[category] ? '−' : '+'}
                  </button>
                </div>
              </div>
            </div>

            {expandedCategories[category] && (
              <div className="p-4">
                <div className="space-y-4">
                  {items.map((item, itemIndex) => (
                    <div key={item._id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start space-x-3">
                        <button
                          onClick={() => handleItemToggle(item._id, !item.checked)}
                          className={`mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                            item.checked 
                              ? 'bg-green-500 border-green-500 text-white' 
                              : 'border-gray-300 hover:border-green-500'
                          }`}
                          disabled={saving}
                        >
                          {item.checked && <CheckCircle className="w-4 h-4" />}
                        </button>

                        <div className="flex-1 min-w-0">
                          <p className={`font-medium ${item.checked ? 'text-green-600 line-through' : 'text-gray-900'}`}>
                            {item.item}
                          </p>
                          
                          <div className="mt-3 space-y-3">
                            {/* Notes Section */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Notes (optional)
                              </label>
                              <textarea
                                value={item.notes || ''}
                                onChange={(e) => handleNotesUpdate(item._id, e.target.value)}
                                placeholder="Add any observations or notes..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                rows="2"
                              />
                            </div>

                            {/* Photo Section */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Photo (optional)
                              </label>
                              {item.photo ? (
                                <div className="flex items-center space-x-3">
                                  <img 
                                    src={item.photo} 
                                    alt="Inspection item" 
                                    className="w-16 h-16 object-cover rounded-lg border"
                                  />
                                  <button
                                    onClick={() => updateChecklistItem(item._id, { photo: '' })}
                                    className="text-red-600 hover:text-red-700 text-sm"
                                  >
                                    Remove Photo
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        handlePhotoUpload(item._id, file);
                                      }
                                    }}
                                    className="hidden"
                                    id={`photo-${item._id}`}
                                  />
                                  <label
                                    htmlFor={`photo-${item._id}`}
                                    className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer text-sm"
                                  >
                                    <Camera className="w-4 h-4" />
                                    <span>Add Photo</span>
                                  </label>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        ))}

        {/* General Notes */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-lg shadow-sm border p-6 mb-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">General Notes</h3>
          <textarea
            value={teamNotes}
            onChange={(e) => setTeamNotes(e.target.value)}
            placeholder="Add any general observations, issues, or recommendations..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows="4"
          />
        </motion.div>

        {/* Submit Button */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-lg shadow-sm border p-6"
        >
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Submit Inspection</h3>
              <p className="text-sm text-gray-500">
                Complete: {stats.completed} of {stats.total} items ({stats.percentage}%)
              </p>
            </div>
            <button
              onClick={handleSubmit}
              disabled={submitting || !teamMemberName.trim()}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                submitting || !teamMemberName.trim()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Submit Inspection</span>
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TeamChecklistPage;